/**
 * Reports (S-A71) — deliberately modest.
 *
 * ===========================================================================
 * WHAT THIS SCREEN WILL NOT DO
 * ===========================================================================
 * There is no analytics endpoint in this product. Every list is cursor-paginated and a
 * cursor page carries no total, so there is no honest way to say "your firm has 412
 * open matters" from the API as it stands.
 *
 * So this screen shows COUNTS OF RECORDS IT ACTUALLY FETCHED, says so in those words,
 * and marks any figure that hit the page ceiling as a floor ("100+"). It contains:
 *
 *   no trends, because nothing here holds a time series;
 *   no percentages against a total the API never returned;
 *   no revenue, because summing decimal strings across currencies is a different job
 *     and a wrong ledger figure is not a display bug;
 *   no averages, no forecasts, no "conversion rate".
 *
 * A report that implies data the system does not hold is worse than no report, because
 * somebody will make a decision on it and nobody will know the number was invented.
 * Every one of the absences above is a deliberate refusal, not an unfinished feature.
 * ===========================================================================
 *
 * SCOPE. Each figure is counted inside the reader's own scope, because the server
 * applies it to the query rather than to the widget. Two people with different roles
 * will see different numbers on this page and both will be right. It is stated once, at
 * the top, rather than caveated on every tile.
 *
 * PERMISSIONS. A card whose module the reader holds nothing for is not rendered AND not
 * fetched. Finance holds no `critical_date` capability at any scope, so Finance does not
 * get an empty risk panel implying the firm has no limitation dates — which is the same
 * `null`-versus-`[]` distinction the case detail payload makes.
 */

import {
  STAFF,
  qk,
  useApiQuery,
  type CaseListItem,
  type CriticalDate,
  type DocumentListItem,
  type InvoiceListItem,
  type Lead,
  type LeadStage,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  Skeleton,
  cn,
} from '@legalcrm/ui';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useCan } from '@/auth/permissions';
import { humanise } from '@/lib/format';
import { PageHeader } from '@/shell/PageHeader';

import { TALLY_LIMIT, tallyBy, useTally, type Tally } from './useTally';

export function ReportsPage() {
  const navigate = useNavigate();

  const canSeeCases = useCan('case', 'view');
  const canSeeLeads = useCan('lead', 'view');
  const canSeeInvoices = useCan('invoice', 'view');
  const canSeeDocuments = useCan('document', 'view');
  const canSeeCriticalDates = useCan('critical_date', 'view');

  return (
    <>
      <PageHeader
        title="Reports"
        description="Counts of what the system holds today. Nothing here is estimated, projected or averaged."
        actions={
          <Button variant="secondary" onClick={() => navigate('/cases')}>
            Open matters
          </Button>
        }
      />

      <div className="mb-4 rounded border bg-surface-sunken px-3 py-2 text-sm text-ink-muted">
        <p>
          <strong className="font-semibold text-ink">These are counts, not metrics.</strong>{' '}
          Each figure is the number of records returned by a list endpoint, counted within
          what you are permitted to see. The API is cursor-paginated and returns no totals,
          so anything that reaches {TALLY_LIMIT} records is shown as{' '}
          <span className="font-medium">{TALLY_LIMIT}+</span> — a floor, not a total.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {canSeeCases && <MattersByStage />}
        {canSeeLeads && <LeadsByStage />}
        {canSeeInvoices && <InvoicePosition />}
        {canSeeDocuments && <DocumentsAwaitingReview />}
        {canSeeCriticalDates && <CriticalDatesByRisk />}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Matters by stage
// ---------------------------------------------------------------------------

function MattersByStage() {
  const tally = useTally<CaseListItem>(qk.list('cases', { report: 'stage' }), STAFF.cases, {
    status: 'open',
  });

  /*
   * Stages are configured PER WORKFLOW — this firm has three, with 11, 13 and 15 stages
   * — so there is no single ordered axis to lay these out along and this is not a
   * funnel. Buckets are therefore the stage names actually observed, biggest first.
   *
   * `stage_name` throughout, never `stage_key`. The key is an internal identifier and
   * the firm configures the name; the portal renders a third string again.
   */
  const observed = new Map<string, string>();
  for (const row of tally.rows) observed.set(row.stage_key, row.stage_name);
  const buckets = tallyBy(
    tally.rows,
    (row) => row.stage_key,
    [...observed.entries()].map(([key, label]) => ({ key, label })),
  ).sort((a, b) => b.value - a.value);

  return (
    <ReportCard
      title="Open matters by stage"
      description="Where the firm's live work is sitting."
      tally={tally}
      footnote="Stages are configured per workflow, so this is a count of matters at each named stage rather than a pipeline. Stages with no open matters are not listed."
      link={{ to: '/cases', label: 'Open the matter list' }}
    >
      <BarList buckets={buckets} total={tally.count} emptyLabel="No open matters." />
    </ReportCard>
  );
}

// ---------------------------------------------------------------------------
// Leads by stage
// ---------------------------------------------------------------------------

function LeadsByStage() {
  const tally = useTally<Lead>(qk.list('leads', { report: 'stage' }), STAFF.leads);

  /** A bare array, not a cursor page — the configured stages, in their own order. */
  const stages = useApiQuery<LeadStage[]>(qk.list('lead-stages'), STAFF.leadStages);

  const order = (stages.data ?? [])
    .filter((stage) => stage.is_active)
    .sort((a, b) => a.position - b.position)
    .map((stage) => ({ key: stage.key, label: stage.name }));

  // Unlike matters, leads share one configured stage set, so a stage holding nothing is
  // meaningful and is kept: "nothing at Proposal" is a finding, not an absence.
  const buckets = tallyBy(tally.rows, (row) => row.stage.key, order);

  return (
    <ReportCard
      title="Leads by stage"
      description="Every configured stage, including the ones holding nothing."
      tally={tally}
      link={{ to: '/leads', label: 'Open the lead list' }}
    >
      <BarList buckets={buckets} total={tally.count} emptyLabel="No leads." />
    </ReportCard>
  );
}

// ---------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------

function InvoicePosition() {
  const tally = useTally<InvoiceListItem>(
    qk.list('invoices', { report: 'position' }),
    STAFF.invoices,
  );

  /*
   * COUNTS OF INVOICES, NOT SUMS OF MONEY.
   *
   * Totals are string decimals and the list mixes currencies, so adding them up needs a
   * currency split and exact decimal arithmetic. A rounding error in a firm's ledger is
   * not a display bug, and a headline figure that is quietly wrong at the fourth decimal
   * place is exactly the kind of number people quote in meetings.
   *
   * Comparing a decimal string against zero IS safe, and is all this needs.
   */
  const outstanding = tally.rows.filter((row) => Number(row.balance) > 0);
  const settled = tally.rows.filter((row) => Number(row.balance) <= 0);
  const overdue = outstanding.filter((row) => row.status === 'overdue');

  const buckets = tallyBy(
    tally.rows,
    (row) => row.status,
    [
      { key: 'draft', label: 'Draft' },
      { key: 'issued', label: 'Issued' },
      { key: 'sent', label: 'Sent' },
      { key: 'partially_paid', label: 'Partially paid' },
      { key: 'overdue', label: 'Overdue' },
      { key: 'paid', label: 'Paid' },
    ],
  );

  return (
    <ReportCard
      title="Invoices outstanding and settled"
      description="Counted by whether a balance is still owed."
      tally={tally}
      footnote="Counts of invoices, not amounts. The list returns per-invoice decimal strings in mixed currencies, and this screen deliberately does not add them up."
      link={{ to: '/invoices', label: 'Open the invoice list' }}
    >
      <div className="mb-4 grid grid-cols-3 gap-3">
        <Figure label="Outstanding" value={outstanding.length} truncated={tally.truncated} />
        <Figure
          label="Overdue"
          value={overdue.length}
          truncated={tally.truncated}
          tone={overdue.length > 0 ? 'danger' : undefined}
        />
        <Figure label="Settled" value={settled.length} truncated={tally.truncated} />
      </div>
      <BarList buckets={buckets} total={tally.count} emptyLabel="No invoices." />
    </ReportCard>
  );
}

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

function DocumentsAwaitingReview() {
  const tally = useTally<DocumentListItem>(
    qk.list('documents', { report: 'status' }),
    STAFF.documents,
  );

  const awaiting = tally.rows.filter((row) => row.status === 'under_review');

  const buckets = tallyBy(
    tally.rows,
    (row) => row.status,
    [
      { key: 'under_review', label: 'Awaiting review' },
      { key: 'pending', label: 'Pending' },
      { key: 'approved', label: 'Approved' },
      { key: 'rejected', label: 'Rejected' },
    ],
  );

  return (
    <ReportCard
      title="Documents awaiting review"
      description="What clients have sent in that nobody has looked at."
      tally={tally}
      link={{ to: '/documents/review', label: 'Open the review queue' }}
    >
      <div className="mb-4">
        <Figure
          label="Awaiting review"
          value={awaiting.length}
          truncated={tally.truncated}
          tone={awaiting.length > 0 ? 'warning' : undefined}
          large
        />
      </div>
      <BarList buckets={buckets} total={tally.count} emptyLabel="No documents." />
    </ReportCard>
  );
}

// ---------------------------------------------------------------------------
// Critical dates
// ---------------------------------------------------------------------------

/**
 * The three exposures, worst first.
 *
 * The same definition the register uses (`CriticalDatesPage.riskOf`). It is repeated
 * rather than shared because the register is the authority on it and this screen is a
 * reader — but the two must not drift, so if the register's bands change, change these.
 */
function riskBand(date: CriticalDate): 'missed' | 'imminent' | 'unverified' | null {
  if (date.missed_at) return 'missed';
  if (date.is_open && date.days_remaining <= 7) return 'imminent';
  if (date.is_open && !date.verified_at) return 'unverified';
  return null;
}

function CriticalDatesByRisk() {
  /*
   * `at-risk` is its own ENDPOINT rather than a filter, because the definition of "at
   * risk" belongs to the domain and not to a query the frontend assembles. Asking the
   * register's own route is what keeps this screen from becoming a second, divergent
   * opinion about the firm's risk position.
   */
  const tally = useTally<CriticalDate>(
    qk.list('critical-dates', { report: 'risk' }),
    STAFF.criticalDatesAtRisk,
  );

  const counts = { missed: 0, imminent: 0, unverified: 0 };
  for (const date of tally.rows) {
    const band = riskBand(date);
    if (band) counts[band] += 1;
  }

  return (
    <ReportCard
      title="Critical dates at risk"
      // The framing travels with the data, on every surface it appears.
      description="The firm's risk register. Never shown to a client, on any surface."
      tally={tally}
      footnote="Missed, inside the seven-day rung, or unverified — the register's own definition, from its own endpoint. This is a pointer to the register, not a substitute for reading it."
      link={{ to: '/critical-dates', label: 'Open the register' }}
      sensitivity="internal"
    >
      <div className="grid grid-cols-3 gap-3">
        <Figure
          label="Missed"
          value={counts.missed}
          truncated={tally.truncated}
          tone={counts.missed > 0 ? 'danger' : undefined}
          note="Already a potential exposure"
        />
        <Figure
          label="Due within 7 days"
          value={counts.imminent}
          truncated={tally.truncated}
          tone={counts.imminent > 0 ? 'danger' : undefined}
          note="Escalating to administrators"
        />
        <Figure
          label="Unverified"
          value={counts.unverified}
          truncated={tally.truncated}
          tone={counts.unverified > 0 ? 'warning' : undefined}
          note="Nobody has checked the calculation"
        />
      </div>
    </ReportCard>
  );
}

// ---------------------------------------------------------------------------
// Presentation
// ---------------------------------------------------------------------------

interface ReportCardProps {
  title: string;
  description: string;
  tally: Tally<unknown>;
  footnote?: string;
  link?: { to: string; label: string };
  sensitivity?: 'internal';
  children: ReactNode;
}

function ReportCard({
  title,
  description,
  tally,
  footnote,
  link,
  sensitivity,
  children,
}: ReportCardProps) {
  return (
    <Card sensitivity={sensitivity}>
      <CardHeader
        title={title}
        description={description}
        actions={
          tally.truncated ? (
            <Badge tone="warning" size="sm">
              First {TALLY_LIMIT} only
            </Badge>
          ) : null
        }
      />
      <CardBody>
        {tally.isLoading && <Skeleton className="h-32 w-full" />}
        {!tally.isLoading && Boolean(tally.error) && (
          // The 404 rule and the 409 rule both live in here. Do not re-word them.
          <ErrorState error={tally.error} onRetry={tally.refetch} size="sm" />
        )}
        {!tally.isLoading && !tally.error && children}

        {!tally.isLoading && !tally.error && footnote && (
          <p className="mt-3 border-t pt-2 text-xs text-ink-muted">{footnote}</p>
        )}
        {link && (
          <p className="mt-2 text-sm">
            <Link to={link.to} className="text-primary hover:underline">
              {link.label}
            </Link>
          </p>
        )}
      </CardBody>
    </Card>
  );
}

function Figure({
  label,
  value,
  truncated,
  tone,
  note,
  large,
}: {
  label: string;
  value: number;
  /** The page ceiling was hit, so this figure is a floor. */
  truncated: boolean;
  tone?: 'danger' | 'warning';
  note?: string;
  large?: boolean;
}) {
  return (
    <div>
      <p className="text-sm text-ink-muted">{label}</p>
      <p
        className={cn(
          'font-semibold tabular-nums',
          large ? 'text-4xl' : 'text-3xl',
          value === 0
            ? 'text-ink-muted'
            : tone === 'danger'
              ? 'text-danger'
              : tone === 'warning'
                ? 'text-warning'
                : 'text-ink',
        )}
      >
        {value}
        {/* The plus is the whole point: it says "at least", not "exactly". */}
        {truncated && <span className="text-xl">+</span>}
      </p>
      {note && <p className="text-xs text-ink-subtle">{note}</p>}
    </div>
  );
}

/**
 * A count per bucket, with a proportional bar.
 *
 * The bar is scaled against the LARGEST BUCKET, not against a firm-wide total, because
 * there is no firm-wide total to scale against. It is a reading aid for comparing the
 * rows on screen and it is never labelled as a percentage of anything.
 */
function BarList({
  buckets,
  total,
  emptyLabel,
}: {
  buckets: Array<{ key: string; label: string; value: number }>;
  total: number;
  emptyLabel: string;
}) {
  if (total === 0) {
    return <p className="text-sm text-ink-subtle">{emptyLabel}</p>;
  }

  const largest = buckets.reduce((max, bucket) => Math.max(max, bucket.value), 0) || 1;

  return (
    <ul className="flex flex-col gap-1.5">
      {buckets.map((bucket) => (
        <li key={bucket.key} className="flex items-center gap-3">
          <span className="w-40 shrink-0 truncate text-sm text-ink" title={bucket.label}>
            {humanise(bucket.label)}
          </span>
          <span className="h-2 flex-1 overflow-hidden rounded-full bg-surface-sunken">
            <span
              className="block h-full rounded-full bg-primary"
              style={{ width: `${(bucket.value / largest) * 100}%` }}
              aria-hidden="true"
            />
          </span>
          <span className="w-10 shrink-0 text-right text-sm tabular-nums text-ink">
            {bucket.value}
          </span>
        </li>
      ))}
    </ul>
  );
}
